<script lang="ts" setup>
import { computed } from 'vue'
import { useTranslate } from '@tolgee/vue'
import type { Impact, ImpactColor, ImpactType, IProject } from '@/api/interfaces'
import { formatTotalAmounts, getCSSVariable } from '@/helpers/utils'
import Decimal from 'decimal.js'

const { t } = useTranslate()

interface Props {
  project: IProject
  colorSettings: Record<ImpactType, ImpactColor>
  impact?: Impact
  active?: boolean
  interactive?: boolean
  compact?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  interactive: false,
  compact: false,
})

const emit = defineEmits<{
  select: []
}>()

const deprecated = computed(() => props.project.status === 'deprecated')
const projectHeader = computed(() => {
  return {
    label: t.value(`${props.project.projectId}.name`),
    name: t.value(`${props.project.projectId}.subName`),
  }
})

const cardTag = computed(() => (props.interactive ? 'button' : 'a'))

const cardAttributes = computed(() =>
  props.interactive
    ? {
        type: 'button',
      }
    : {
        href: props.project.link || undefined,
        target: '_blank',
        rel: 'noreferrer',
      },
)

const handleClick = () => {
  if (props.interactive) {
    emit('select')
  }
}

const impactDisplayAmount = computed(() => {
  const impact = props.impact
  if (!impact) return 0
  if (impact.type === 'carbon') return new Decimal(impact.amount).dividedBy(1000).toNumber()
  return impact.amount
})
</script>

<template>
  <v-tooltip :text="t('PublicProjectCard.deprecated_tooltip')" location="top" max-width="220px">
    <template v-slot:activator="{ props: tooltipProps }">
      <component
        :is="cardTag"
        :class="['project-card', { active }, { compact }]"
        :style="{
          borderColor:
            active && !deprecated
              ? colorSettings[project.type].backgroundColor
              : getCSSVariable('gray'),
        }"
        v-bind="{ ...cardAttributes, ...(deprecated ? tooltipProps : {}) }"
        @click="handleClick"
      >
        <div class="project-card-content">
          <span
            v-if="(deprecated || impact) && $route.name !== 'CommunityPage'"
            :class="['project-card-impact-values', { deprecated }]"
            :style="{
              color: colorSettings[project.type].textColor,
              backgroundColor:
                impact && !deprecated
                  ? colorSettings[project.type].backgroundColor
                  : getCSSVariable('gray'),
            }"
          >
            <template v-if="deprecated">
              {{ t('PublicProjectCard.deprecated') }}
            </template>
            <span v-if="deprecated && impact"> - </span>
            <template v-if="impact">
              {{
                t(`PublicProjectCard.${impact.type}`, {
                  count: formatTotalAmounts(impactDisplayAmount),
                })
              }}
            </template>
          </span>
          <p
            :style="{ color: colorSettings[project.type].backgroundColor }"
            class="project-card-title"
          >
            {{ projectHeader.label }}
          </p>
          <h3 class="project-card-text">
            {{ projectHeader.name }}
          </h3>
        </div>
        <img
          :alt="project.name"
          :src="project.thumbnailImageUrl || project.imageUrl"
          class="project-card-image"
          loading="lazy"
        />
      </component>
    </template>
  </v-tooltip>
</template>

<style lang="scss" scoped>
.project-card {
  background-color: var(--ui-white);
  border-radius: 10px;
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--gray);
  box-shadow: 2px 2px 20px 0 rgba(0 0 0 / 10%);
  text-align: left;
  text-decoration: none;
  max-width: 280px;
  transition:
    border-color 200ms ease-in-out,
    transform 200ms ease-in-out;
  width: 100%;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:hover {
    border-color: var(--ui-green);
  }

  &.active {
    border-width: 1px;

    @include media('md-and-up') {
      transform: scale(1.12);
    }
  }

  &.compact {
    padding: 12px;
    min-height: 123px;
    width: 100%;
    max-width: 100%;

    @include media('md-and-up') {
      min-height: 123px;
      max-width: 280px;
    }
  }
}

.project-card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.project-card-impact-values {
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  color: var(--ui-black);
}

.project-card-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.project-card-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--ui-black);
  line-height: 1.2;
}

.project-card-image {
  height: 56px;
  width: 56px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;

  @include media('md-and-up') {
    height: 60px;
    width: 60px;
  }
}

.compact {
  .project-card-title {
    font-size: 10px;
  }

  .project-card-text {
    display: -webkit-box;
    line-clamp: 3;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
}
</style>
